import { useIsMotionReduced } from '../hooks/useIsMotionReduced.js';
import { useCueSetting, usePollCycle } from '../sync/usePollCycle.js';

import type { PollCycle } from '../sync/pollCycle.js';
import type { CSSProperties, ReactElement } from 'react';

/**
 * The one accent in the app that is never a channel colour, so the cue cannot
 * be mistaken for something a DJ is doing. The themed coral rather than the
 * literal one: at this size the raw `--palette-coral` on the cream canvas is a
 * pastel smudge nobody finds, and the token darkens it for exactly that.
 */
const CUE_INK = 'text-brand-text';
const TRACK_WIDTH_PX = 36;
const TRACK_HEIGHT_PX = 4;
/**
 * The empty track has to stay a shape in its own right: it is what says there
 * is a cue here at all in the moment the bar has run all the way down.
 */
const TRACK_ALPHA = 0.22;
const MS_PER_SECOND = 1000;

const remainingOf = (cycle: PollCycle, isMotionReduced: boolean): CSSProperties => {
  if (isMotionReduced) {
    return { width: '100%' };
  }

  // The failure state is the same bar saying something else: run down to
  // nothing and held there. No alert, no modal — the screen is still showing
  // the last thing it knew, which is all it can honestly show.
  if (cycle.stale) {
    return { width: '0%' };
  }

  // No CSS transition: the 100ms tick is already smooth, and a transition
  // would drag the refill back across the bar at the top of every cycle.
  return { width: `${String((1 - cycle.phase) * 100)}%` };
};

/**
 * The bar that says the data on screen is alive, and how much of the current
 * answer's life is left.
 *
 * It exists because everything else on these pages is deliberately silent about
 * fetching: no spinner, no dimming, no "last updated" string, because a
 * projector in a room full of people must never flicker and a DJ must never be
 * told to wait. That leaves a real question unanswered — is this screen still
 * following the party, or did it stop ten minutes ago? — and this answers it
 * peripherally, by draining rather than filling: length left *is* time left, so
 * it is read as a width at a glance instead of judged as a brightness. An empty
 * track that stays empty is a screen that has stopped hearing back.
 * One per page, beside the party name.
 */
type SyncMeterProps = {
  /** Coral on a channel-coloured band reads as a muddy dash; there the cue is drawn in the band's own ink. */
  readonly onColour?: boolean;
};

export const SyncMeter = ({ onColour = false }: SyncMeterProps): ReactElement | null => {
  const { isCueEnabled } = useCueSetting();
  const isMotionReduced = useIsMotionReduced();
  const cycle = usePollCycle(isCueEnabled && !isMotionReduced);

  if (!isCueEnabled) {
    return null;
  }

  const seconds = Math.round(cycle.intervalMs / MS_PER_SECOND);

  return (
    <span
      aria-hidden="true"
      title={`Live data · refreshes every ${String(seconds)}s`}
      className="relative shrink-0 overflow-hidden rounded-full"
      style={{ width: TRACK_WIDTH_PX, height: TRACK_HEIGHT_PX }}
    >
      <span
        className="absolute inset-0 rounded-full"
        style={{ backgroundColor: 'currentColor', opacity: TRACK_ALPHA }}
      />
      <span
        className={`absolute inset-y-0 left-0 rounded-full ${onColour ? '' : CUE_INK}`}
        style={{
          backgroundColor: 'currentColor',
          ...remainingOf(cycle, isMotionReduced),
        }}
      />
    </span>
  );
};
